import { DateTime } from 'luxon';

export interface IsExternalAgentBackdateInput {
  effectiveDate: string;
  isInternalUser: boolean;
  policyTimeZone?: string;
}

/**
 * Returns `true` when an external agent's candidate effectiveDate is before
 * the start of today in the policy's timezone (i.e. a disallowed backdate).
 *
 * Pure predicate with no side effects — kept separate from
 * `validateDraftEffectiveDate` (which sets the inline picker error) so the
 * apply-time modal flow stays decoupled from the inline error state.
 */
export function isExternalAgentBackdate({
  effectiveDate,
  isInternalUser,
  policyTimeZone,
}: IsExternalAgentBackdateInput): boolean {
  if (isInternalUser) return false;

  const zone =
    policyTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Parse directly in the policy timezone so bare dates and full ISO
  // timestamps both anchor at policy-TZ midnight.
  const effective = DateTime.fromISO(effectiveDate, { zone }).startOf('day');

  // Fail closed when the effectiveDate is unparseable. Luxon returns an
  // invalid DateTime that compares as `false` against a valid one, so
  // without this guard an empty or malformed string would silently bypass
  // the block.
  if (!effective.isValid) return true;

  const todayStart = DateTime.now().setZone(zone).startOf('day');

  return effective < todayStart;
}
